/**
 * One workout, painted for a screen on the studio wall.
 *
 * FIXED STAGE, SCALED BY THE CALLER. Everything here is laid out against a constant 1600x900 box
 * and never reflows — the caller scales the whole node with a CSS transform to fill whatever TV it
 * lands on ({@link ProgramBoardStage}). A responsive board is the wrong shape for this: on a wall
 * screen a breakpoint that drops to one column produces a board nobody can read from the floor,
 * and the same layout has to come out of the PDF export byte-identical to what is on the screen.
 * With a fixed stage, card count is the only thing that changes the type size, and that decision
 * lives in `getBoardLayout`.
 *
 * PLAIN DIVS, NOT MUI. The board is captured by html2canvas for the PDF/PNG export, and it is the
 * one surface in this admin that is not wearing the admin's own theme — it wears the STUDIO's
 * (logo + brand colour). Inline styles keep both of those honest.
 */

import React, { useState } from 'react';
import { imageUrl } from '../../../utils/imageUrl';
import { getBoardLayout, imageFit } from './programBoardModel';
import type { BoardCard, BoardCardExercise, BoardDay } from './programBoardModel';

/** The studio's identity on the board. `color` is the only one with no sensible blank state. */
export interface BoardBrand {
    logoUrl?: string | null;
    color: string;
    name?: string | null;
}

export interface ProgramBoardProps {
    day: BoardDay;
    /** The program the workout belongs to — the small line above the workout's own name. */
    programName?: string | null;
    brand: BoardBrand;
    /** Set on the node the exporter captures. */
    innerRef?: React.Ref<HTMLDivElement>;
}

/** The stage the whole board is laid out against, in px. 16:9, the shape of every TV. */
export const STAGE_WIDTH = 1600;
export const STAGE_HEIGHT = 900;

const INK = {
    bg: '#0A0F1C',
    panel: '#131A2B',
    panelEdge: 'rgba(255,255,255,0.08)',
    text: '#FFFFFF',
    muted: '#93A2C0',
    imageBg: '#0E1524',
};

/** A colour mixed with the stage background — brand accents at a readable opacity on dark. */
const tint = (color: string, alpha: number): string => {
    const hex = color.replace('#', '');
    if (hex.length !== 6) return `rgba(1,187,167,${alpha})`;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
};

const ExerciseBlock: React.FC<{
    exercise: BoardCardExercise;
    layout: ReturnType<typeof getBoardLayout>;
    brand: BoardBrand;
    /** A superset packs two of these into one card, so each gets half the room. */
    compact?: boolean;
}> = ({ exercise, layout, brand, compact = false }) => {
    // Starts as `cover` and corrects itself once the browser knows the picture's shape — see
    // imageFit. Landscape frames therefore never flash a letterbox on their way in.
    const [fit, setFit] = useState<'cover' | 'contain'>('cover');
    const nameSize = compact ? layout.nameSize * 0.78 : layout.nameSize;
    const valueSize = compact ? layout.valueSize * 0.72 : layout.valueSize;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 8 }}>
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    borderRadius: 12,
                    overflow: 'hidden',
                    background: INK.imageBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {exercise.imageUrl ? (
                    <img
                        // dpr:2 explicitly, rather than the screen's. A wall TV reports dpr 1, but
                        // the stage is scaled UP to fill it — a variant sized for the CSS box would
                        // arrive soft on exactly the screen this board exists for.
                        src={imageUrl(exercise.imageUrl, 480, { dpr: 2 }) || exercise.imageUrl}
                        alt={exercise.name}
                        onLoad={event =>
                            setFit(imageFit(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight))
                        }
                        style={{ width: '100%', height: '100%', objectFit: fit }}
                    />
                ) : (
                    // No picture is better than a broken one: a program can reference an exercise
                    // whose media was never uploaded, and the card still has to hold its shape.
                    <span style={{ color: INK.muted, fontSize: layout.detailSize }}>ללא תמונה</span>
                )}
            </div>

            <div style={{ fontSize: nameSize, fontWeight: 800, lineHeight: 1.15, color: INK.text }}>
                {exercise.name}
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: valueSize, fontWeight: 900, color: brand.color, lineHeight: 1 }}>
                        {exercise.prescription.value}
                    </span>
                    <span style={{ fontSize: layout.unitSize, fontWeight: 600, color: INK.muted }}>
                        {exercise.prescription.unit}
                    </span>
                </div>
                {exercise.detail && (
                    <span style={{ fontSize: layout.detailSize, color: INK.muted, whiteSpace: 'nowrap' }}>
                        {exercise.detail}
                    </span>
                )}
            </div>

            {exercise.emphasis && (
                <div
                    style={{
                        alignSelf: 'flex-start',
                        fontSize: layout.detailSize,
                        fontWeight: 700,
                        color: brand.color,
                        background: tint(brand.color, 0.14),
                        border: `1px solid ${tint(brand.color, 0.32)}`,
                        borderRadius: 999,
                        padding: '2px 10px',
                    }}
                >
                    {exercise.emphasis}
                </div>
            )}
        </div>
    );
};

const Card: React.FC<{
    card: BoardCard;
    layout: ReturnType<typeof getBoardLayout>;
    brand: BoardBrand;
}> = ({ card, layout, brand }) => (
    <div
        style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            background: INK.panel,
            border: `1px solid ${INK.panelEdge}`,
            borderRadius: 18,
            padding: layout.gap * 0.7,
            gap: 10,
        }}
    >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
                style={{
                    width: layout.badgeSize,
                    height: layout.badgeSize,
                    borderRadius: '50%',
                    background: brand.color,
                    color: '#04121A',
                    fontSize: layout.badgeSize * 0.5,
                    fontWeight: 900,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                {card.number}
            </div>
            {card.type === 'superset' && (
                <div
                    style={{
                        fontSize: layout.detailSize,
                        fontWeight: 800,
                        color: '#C4B5FD',
                        background: 'rgba(124,58,237,0.18)',
                        border: '1px solid rgba(124,58,237,0.45)',
                        borderRadius: 999,
                        padding: '3px 12px',
                    }}
                >
                    סופרסט — ללא הפסקה בין השניים
                </div>
            )}
        </div>

        <div
            style={{
                display: 'flex',
                flexDirection: card.type === 'superset' ? 'row' : 'column',
                gap: 12,
                flex: 1,
                minHeight: 0,
            }}
        >
            {card.exercises.map(exercise => (
                <ExerciseBlock
                    key={exercise.key}
                    exercise={exercise}
                    layout={layout}
                    brand={brand}
                    compact={card.type === 'superset'}
                />
            ))}
        </div>
    </div>
);

export const ProgramBoard: React.FC<ProgramBoardProps> = ({ day, programName, brand, innerRef }) => {
    const layout = getBoardLayout(day.cards.length);

    return (
        <div
            ref={innerRef}
            data-testid="program-board"
            style={{
                width: STAGE_WIDTH,
                height: STAGE_HEIGHT,
                background: INK.bg,
                backgroundImage: `radial-gradient(120% 90% at 100% 0%, ${tint(brand.color, 0.18)} 0%, rgba(10,15,28,0) 55%)`,
                color: INK.text,
                fontFamily: '"Heebo", "Rubik", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
                display: 'flex',
                flexDirection: 'column',
                padding: 36,
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            {/* Header — logo, what this workout is, what it asks for */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, paddingBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 220 }}>
                    {brand.logoUrl ? (
                        <img
                            src={brand.logoUrl}
                            alt={brand.name || ''}
                            style={{ height: 54, maxWidth: 220, objectFit: 'contain' }}
                        />
                    ) : (
                        brand.name && (
                            <span style={{ fontSize: 26, fontWeight: 900, color: brand.color }}>{brand.name}</span>
                        )
                    )}
                </div>

                <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                    {programName && (
                        <div style={{ fontSize: 18, fontWeight: 600, color: INK.muted, letterSpacing: '0.04em' }}>
                            {programName}
                        </div>
                    )}
                    <div style={{ fontSize: 40, fontWeight: 900, lineHeight: 1.15 }}>{day.title}</div>
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-start', minWidth: 220, flexWrap: 'wrap' }}>
                    {day.stats.map(stat => (
                        <span
                            key={stat}
                            style={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: INK.text,
                                background: 'rgba(255,255,255,0.07)',
                                border: `1px solid ${INK.panelEdge}`,
                                borderRadius: 999,
                                padding: '6px 14px',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {stat}
                        </span>
                    ))}
                </div>
            </div>

            <div style={{ height: 2, background: tint(brand.color, 0.55), borderRadius: 2, marginBottom: 18 }} />

            {/* The workout itself */}
            {day.cards.length === 0 ? (
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 28,
                        color: INK.muted,
                    }}
                >
                    אין תרגילים באימון הזה
                </div>
            ) : (
                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: 'grid',
                        gridTemplateColumns: `repeat(${layout.columns}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${layout.rows}, minmax(0, 1fr))`,
                        gap: layout.gap,
                    }}
                >
                    {day.cards.map(card => (
                        <Card key={card.number} card={card} layout={layout} brand={brand} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProgramBoard;
